import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, User } from "lucide-react";
import { useApiResource } from "../api/useApi";
import { getAthleteClips, getRankings, frameThumbUrl } from "../api/resources";
import type { AthleteClipItem, RankingItem } from "../api/resources";
import { ACTIVITY_EMOJI, ACTIVITY_LABEL, ActivityId } from "../mockData";
import { TierBadge } from "../components/TierBadge";

/**
 * Athlete gallery — the drill-down behind a rankings row: every clip in this athlete's
 * score history, as a card grid. Public-within-the-app v1 (same visibility as the
 * rankings list it hangs off); per-upload visibility metadata + premium privacy gating
 * is the named successor.
 *
 * Data contract: the header (name, tier, hybrid + per-technique averages) comes from the
 * SAME /platform/rankings response the rankings page renders, and the cards come from
 * /platform/athleteClips — which the backend builds from the SAME scorecard scan as the
 * rankings. The two views can never disagree about which clips exist or what they scored.
 *
 * Card visuals: the preview is the clip's REAL first frame — uploads store
 * {previewFileId}.png, the split-frame images the renderer prefetches. {sfcId}.png is NOT
 * a frame of the clip (it can be an unrelated asset) and is never requested. A null
 * previewFileId (no frames resolvable) goes straight to the sessions-list emoji, and a
 * failed load swaps to it too. A card opens /sessions/:id — exactly how the sessions list
 * opens an upload today.
 */

const fmtScore = (v?: number | null) => (v == null ? "—" : v.toFixed(1));
const fmtDate = (iso?: string | null) => (iso ? iso.slice(0, 10) : "—");

function techniqueShortLabel(sportId?: string | null): string {
  if (!sportId) return "Unknown";
  return ACTIVITY_LABEL[sportId as ActivityId] ?? sportId;
}

function ClipCard({ clip }: { clip: AthleteClipItem }) {
  // No previewFileId (pre-split-store upload / failed lookup) or a failed load swaps to
  // the sessions-list emoji rather than leaving a broken image. NEVER {sfcId}.png — that
  // file is not a frame of the clip.
  const [imgFailed, setImgFailed] = useState(false);
  const emoji = ACTIVITY_EMOJI[(clip.sportId ?? "unknown") as ActivityId] ?? "🎬";
  const showFrame = clip.previewFileId != null && !imgFailed;

  return (
    <div
      data-testid="gallery-card"
      className="card p-0 overflow-hidden hover:shadow-md transition group"
    >
      <Link
        to={`/sessions/${clip.uploadId}`}
        data-testid="gallery-card-link"
        className="block"
        title="Open this session"
      >
        <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
          {showFrame ? (
            <img
              data-testid="gallery-thumb"
              src={frameThumbUrl(clip.previewFileId as number)}
              alt={`Session ${clip.uploadId} preview`}
              loading="lazy"
              className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <div data-testid="gallery-thumb-fallback" className="text-4xl">
              {emoji}
            </div>
          )}
        </div>
        <div className="p-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-slate-800 truncate">
              {techniqueShortLabel(clip.sportId)}
            </div>
            {(clip.finalScore ?? clip.ruleOverallScore) != null ? (
              <span
                data-testid="gallery-score"
                className="inline-flex items-center rounded-full bg-el-50 border border-el-200 px-2 py-0.5 text-xs font-semibold text-el-800"
              >
                {(clip.finalScore ?? clip.ruleOverallScore)!.toFixed(1)}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-slate-50 border border-slate-200 px-2 py-0.5 text-[0.7rem] font-medium text-slate-500">
                Unscored
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
            <span>{fmtDate(clip.analyzedAtUtc)}</span>
            <TierBadge tier={clip.tier} />
          </div>
          {clip.eventName && (
            <div className="text-xs text-slate-600 truncate">
              {clip.eventName}
              {clip.eventDate ? ` · ${clip.eventDate}` : ""}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}

export default function AthleteGallery() {
  const { contactId } = useParams();
  const contactIdNum = Number(contactId);

  const { data: clipsResp, isLoading: clipsLoading, error: clipsError } = useApiResource(
    (apiFetch) => getAthleteClips(apiFetch, contactIdNum),
    [contactIdNum]
  );
  // Header data comes from the SAME rankings response the rankings page shows — no second
  // aggregate to drift from it.
  const { data: rankingsResp } = useApiResource(
    (apiFetch) => getRankings(apiFetch),
    []
  );

  const clips: AthleteClipItem[] = useMemo(() => clipsResp?.values ?? [], [clipsResp]);
  const athlete: RankingItem | undefined = useMemo(
    () => (rankingsResp?.values ?? []).find((r) => r.contactId === contactIdNum),
    [rankingsResp, contactIdNum]
  );

  return (
    <div className="space-y-4">
      <div data-testid="gallery-header" className="space-y-2">
        <Link
          to="/rankings"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-el-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Rankings
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
            <User className="h-5 w-5 text-slate-500" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-slate-900">
                {athlete?.name || `Athlete #${contactIdNum}`}
              </span>
              <TierBadge tier={athlete?.tier} />
            </div>
            <div className="text-xs text-slate-500">
              <span data-testid="gallery-hybrid">
                Overall {fmtScore(athlete?.hybridScore)}
              </span>
              {" · "}
              {athlete?.clipCount ?? clips.length} clips
            </div>
          </div>
        </div>
        {athlete && athlete.perTechnique?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {athlete.perTechnique.map((t) => (
              <span
                key={t.sportId}
                data-testid="gallery-technique-avg"
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600"
              >
                {techniqueShortLabel(t.sportId)}
                <span className="font-semibold text-slate-800">{fmtScore(t.averageScore)}</span>
                <span className="text-slate-400">· {t.clipCount} clips</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {clipsLoading && (
        <div
          data-testid="gallery-loading"
          className="flex items-center gap-2 justify-center py-12 text-sm text-slate-500"
        >
          <Loader2 className="h-5 w-5 animate-spin text-el-600" /> Loading clips…
        </div>
      )}

      {!clipsLoading && clipsError && (
        <div className="text-sm text-rose-600 py-8 text-center">
          Couldn't load this athlete's clips — try again.
        </div>
      )}

      {!clipsLoading && !clipsError && clips.length === 0 && (
        <div data-testid="gallery-empty" className="text-center py-12 space-y-1">
          <div className="text-sm font-medium text-slate-700">No analyzed clips yet.</div>
          <div className="text-xs text-slate-500 max-w-md mx-auto">
            Clips appear here once they're analyzed and scored — the same set the rankings
            count for this athlete.
          </div>
        </div>
      )}

      {!clipsLoading && !clipsError && clips.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {clips.map((clip) => (
            <ClipCard key={clip.uploadId} clip={clip} />
          ))}
        </div>
      )}
    </div>
  );
}
